import { useState, useCallback } from "react";
import { useAdmin } from "@/context/AdminContext";
import { useDarkMode } from "@/context/DarkModeContext";
import { useAdminStorage } from "@/hooks/useAdminStorage";
import { useLogoFont } from "@/hooks/useLogoFont";
import AdminEditField from "@/components/feature/AdminEditField";
import RichText from "@/components/base/RichText";
import { defaultFAQ, type FAQSectionData, type FAQItem } from "@/data/siteContent";

interface FAQSectionProps { dataKey?: string; }

export default function FAQSection({ dataKey }: FAQSectionProps = {}) {
  const { isEditing: isAdmin } = useAdmin();
  const { isDark } = useDarkMode();
  const { fontFamily: headingFont, style: headingStyle } = useLogoFont();
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const [data, setData] = useAdminStorage<FAQSectionData>(dataKey ?? "ma_faq_v2", defaultFAQ);

  const set = <K extends keyof FAQSectionData>(key: K, val: FAQSectionData[K]) => {
    setData({ ...data, [key]: val });
  };

  const updateItem = (id: string, field: "q" | "a" | "listItems" | "contentType", val: string | string[]) => {
    const items = data.items.map((item) => item.id === id ? { ...item, [field]: val } : item);
    setData({ ...data, items });
  };

  const addItem = (type: FAQItem["type"] = "qa", afterIndex?: number) => {
    const newItem: FAQItem = type === "qa"
      ? { id: `faq-${Date.now()}`, q: "New Question", a: "Your answer here.", type: "qa" }
      : type === "heading"
      ? { id: `faq-h-${Date.now()}`, q: "New Heading", a: "", type: "heading" }
      : type === "text"
      ? { id: `faq-t-${Date.now()}`, q: "", a: "Click to edit this text blurb.", type: "text", contentType: "paragraph" }
      : { id: `faq-d-${Date.now()}`, q: "", a: "", type: "divider" };

    const items = [...data.items];
    const insertIdx = afterIndex !== undefined ? afterIndex + 1 : items.length;
    items.splice(insertIdx, 0, newItem);
    setData({ ...data, items });
    setOpenIndex(null);
  };

  const removeItem = (id: string) => {
    setData({ ...data, items: data.items.filter((item) => item.id !== id) });
    setOpenIndex(null);
  };

  const moveItem = (idx: number, dir: -1 | 1) => {
    const items = [...data.items];
    const target = idx + dir;
    if (target < 0 || target >= items.length) return;
    [items[idx], items[target]] = [items[target], items[idx]];
    setData({ ...data, items });
  };

  const updateStat = (idx: number, field: "value" | "label", val: string) => {
    const stats = data.stats.map((s, i) => i === idx ? { ...s, [field]: val } : s);
    setData({ ...data, stats });
  };

  const addStat = () => {
    setData({ ...data, stats: [...data.stats, { value: "New", label: "Stat" }] });
  };

  const removeStat = (idx: number) => {
    setData({ ...data, stats: data.stats.filter((_, i) => i !== idx) });
  };

  const btnBase = isDark
    ? "w-7 h-7 flex items-center justify-center rounded-md bg-gray-700 hover:bg-gray-600 text-gray-400 hover:text-gray-300 cursor-pointer disabled:opacity-30 transition-colors border border-gray-600"
    : "w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-30 transition-colors border border-gray-200";

  const iconBase = isDark
    ? "w-7 h-7 flex items-center justify-center rounded-md bg-gray-700 text-gray-400 cursor-pointer disabled:opacity-30 transition-colors border border-gray-600"
    : "w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 text-gray-400 cursor-pointer disabled:opacity-30 transition-colors border border-gray-200";

  const scrollTo = (id: string) => {
    document.querySelector(id)?.scrollIntoView({ behavior: "smooth" });
  };

  const bg = isDark ? "bg-gray-900" : "bg-white";
  const headingColor = isDark ? "text-white" : "text-gray-900";
  const subColor = isDark ? "text-gray-400" : "text-gray-500";
  const cardBase = isDark ? "border-gray-700 bg-gray-800" : "border-gray-100 bg-white";
  const cardActive = isDark ? "border-pink-500 bg-gray-700" : "border-pink-300 bg-pink-50";
  const bodyColor = isDark ? "text-gray-300" : "text-gray-600";
  const inputBase = isDark
    ? "w-full border border-gray-600 rounded-xl px-4 py-3 text-sm bg-gray-700 text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-pink-400"
    : "w-full border border-gray-200 rounded-xl px-4 py-3 text-sm text-gray-800 placeholder-gray-300 focus:outline-none focus:ring-2 focus:ring-pink-200";
  const emptyBg = isDark ? "bg-gray-800" : "bg-pink-50";

  // ── Admin Controls for heading/text/divider blocks — always visible with labels ──
  const BlockControls = ({ id, idx }: { id: string; idx: number }) => (
    <div className="flex items-center gap-1.5 flex-shrink-0 mr-2">
      <button
        onClick={() => moveItem(idx, -1)}
        disabled={idx === 0}
        className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-30 transition-colors border border-gray-200"
        title="Move up"
      >
        <i className="ri-arrow-up-line text-xs"></i>
      </button>
      <button
        onClick={() => moveItem(idx, 1)}
        disabled={idx === data.items.length - 1}
        className="w-7 h-7 flex items-center justify-center rounded-md bg-gray-100 hover:bg-gray-200 text-gray-400 hover:text-gray-600 cursor-pointer disabled:opacity-30 transition-colors border border-gray-200"
        title="Move down"
      >
        <i className="ri-arrow-down-line text-xs"></i>
      </button>
      <button
        onClick={() => removeItem(id)}
        className="flex items-center gap-1 text-xs font-bold bg-red-500 hover:bg-red-600 text-white px-3 py-1.5 rounded-full cursor-pointer whitespace-nowrap shadow-sm transition-colors"
        title="Delete this block"
      >
        <i className="ri-delete-bin-line text-xs"></i>
        Delete
      </button>
    </div>
  );

  return (
    <section id="faq" className={`py-24 px-6 transition-colors duration-300 ${bg}`}>
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left */}
          <div className="w-full lg:w-2/5 flex-shrink-0">
            <div
              className={`rounded-3xl p-10 flex flex-col items-center justify-center text-center h-64 relative ${isDark ? "bg-gray-800" : ""}`}
              style={isDark ? {} : { background: "linear-gradient(135deg, #f9a8d4 0%, #86efac 100%)" }}
            >
              <div className="w-20 h-20 flex items-center justify-center bg-white/30 rounded-full mb-4">
                <i className="ri-question-line text-white text-4xl"></i>
              </div>
              {isAdmin ? (
                <AdminEditField
                  value={data.gotQuestionsHeading ?? "Got Questions?"}
                  onSave={(v) => set("gotQuestionsHeading" as keyof FAQSectionData, v as FAQSectionData[keyof FAQSectionData])}
                  inputClassName="text-2xl font-bold text-center text-white"
                >
                  <p className="text-white text-2xl font-bold" style={{ fontFamily: headingFont, ...headingStyle }}>
                    {data.gotQuestionsHeading ?? "Got Questions?"}
                  </p>
                </AdminEditField>
              ) : (
                <p className="text-white text-2xl font-bold" style={{ fontFamily: headingFont, ...headingStyle }}>
                  {data.gotQuestionsHeading ?? "Got Questions?"}
                </p>
              )}
              {isAdmin ? (
                <AdminEditField
                  value={data.gotQuestionsSubtext ?? "We've got answers below"}
                  onSave={(v) => set("gotQuestionsSubtext" as keyof FAQSectionData, v as FAQSectionData[keyof FAQSectionData])}
                  className="mt-2"
                  inputClassName="text-sm text-center text-white/80"
                >
                  <p className="text-white/80 text-sm mt-2">{data.gotQuestionsSubtext ?? "We've got answers below"}</p>
                </AdminEditField>
              ) : (
                <p className="text-white/80 text-sm mt-2">{data.gotQuestionsSubtext ?? "We've got answers below"}</p>
              )}
            </div>

            <div className="mt-8">
              {(isAdmin || data.eyebrow) && (
                isAdmin ? (
                  <AdminEditField value={data.eyebrow} onSave={(v) => set("eyebrow", v)} onDelete={() => set("eyebrow", "")} inputClassName="text-pink-500 text-sm font-bold uppercase tracking-widest">
                    <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
                  </AdminEditField>
                ) : (
                  <span className="text-pink-500 text-sm font-bold uppercase tracking-widest">{data.eyebrow}</span>
                )
              )}
              {(isAdmin || data.heading) && (
                isAdmin ? (
                  <AdminEditField value={data.heading} onSave={(v) => set("heading", v)} onDelete={() => set("heading", "")} className="mt-3 mb-4" inputClassName="text-3xl font-extrabold">
                    <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                      {data.heading}
                    </h2>
                  </AdminEditField>
                ) : (
                  <h2 className={`text-3xl md:text-4xl font-extrabold mt-3 mb-4 ${headingColor}`} style={{ fontFamily: headingFont, ...headingStyle }}>
                    {data.heading}
                  </h2>
                )
              )}
              {(isAdmin || data.subtext) && (
                <p className={`text-sm mb-6 ${subColor}`}>
                  {isAdmin ? (
                    <AdminEditField value={data.subtext} onSave={(v) => set("subtext", v)} onDelete={() => set("subtext", "")} inputClassName="text-sm">
                      <span>{data.subtext}</span>
                    </AdminEditField>
                  ) : (
                    <span>{data.subtext}</span>
                  )}{" "}
                  <a
                    href="#contact"
                    onClick={(e) => { e.preventDefault(); document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" }); }}
                    className="text-pink-500 font-semibold hover:underline cursor-pointer"
                  >
                    {isAdmin ? (
                      <AdminEditField value={data.contactLinkLabel} onSave={(v) => set("contactLinkLabel", v)} onDelete={() => set("contactLinkLabel", "")} inputClassName="text-sm font-semibold text-pink-500">
                        <span>{data.contactLinkLabel}</span>
                      </AdminEditField>
                    ) : (
                      data.contactLinkLabel
                    )}
                  </a>
                </p>
              )}

              {/* Quick stats */}
              <div className="grid grid-cols-2 gap-4">
                {data.stats.map((s, i) => (
                  <div key={i} className={`rounded-xl p-4 text-center relative group/stat ${isDark ? "bg-gray-700" : "bg-pink-50"}`}>
                    {isAdmin ? (
                      <>
                        <AdminEditField value={s.value} onSave={(v) => updateStat(i, "value", v)} inputClassName="text-xl font-extrabold text-pink-500 text-center">
                          <div className="text-xl font-extrabold text-pink-500">{s.value}</div>
                        </AdminEditField>
                        <AdminEditField value={s.label} onSave={(v) => updateStat(i, "label", v)} inputClassName="text-xs text-center">
                          <div className={`text-xs mt-1 ${subColor}`}>{s.label}</div>
                        </AdminEditField>
                        <button
                          onClick={() => removeStat(i)}
                          className="absolute top-1 right-1 w-4 h-4 flex items-center justify-center text-red-300 hover:text-red-500 cursor-pointer rounded-full hover:bg-red-50 opacity-0 group-hover/stat:opacity-100 transition-opacity"
                          title="Remove stat"
                        >
                          <i className="ri-close-line text-xs"></i>
                        </button>
                      </>
                    ) : (
                      <>
                        <div className="text-xl font-extrabold text-pink-500">{s.value}</div>
                        <div className={`text-xs mt-1 ${subColor}`}>{s.label}</div>
                      </>
                    )}
                  </div>
                ))}
                {isAdmin && (
                  <button
                    onClick={addStat}
                    className={`rounded-xl p-4 text-center border-2 border-dashed cursor-pointer transition-colors ${isDark ? "border-gray-600 text-gray-500 hover:border-pink-400 hover:text-pink-400" : "border-pink-200 text-gray-400 hover:border-pink-400 hover:text-pink-500"}`}
                  >
                    <i className="ri-add-line text-lg"></i>
                    <div className="text-xs mt-1">Add stat</div>
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* Right — Mixed content */}
          <div className="w-full lg:w-3/5 space-y-1">
            {/* Only render insert bars when in admin */}
            {isAdmin && <InsertBar afterIndex={-1} />}

            {data.items.map((item, i) => (
              <div key={item.id}>
                {/* Heading block */}
                {item.type === "heading" ? (
                  <div className={`flex items-center gap-2 py-2 rounded-lg ${isAdmin ? `px-2 ${isDark ? "bg-gray-800/60 border border-gray-700" : "bg-gray-50 border border-gray-200"}` : ""}`}>
                    {isAdmin && <BlockControls id={item.id} idx={i} />}
                    {isAdmin ? (
                      <AdminEditField
                        value={item.q}
                        onSave={(v) => updateItem(item.id, "q", v)}
                        inputClassName="text-lg font-bold text-gray-900"
                        richText
                        className="flex-1"
                      >
                        <div className={`text-lg font-bold ${headingColor}`} dangerouslySetInnerHTML={{ __html: item.q }} />
                      </AdminEditField>
                    ) : (
                      <div className={`text-lg font-bold ${headingColor}`} dangerouslySetInnerHTML={{ __html: item.q }} />
                    )}
                  </div>
                ) : item.type === "text" ? (
                  /* Text/List blurb block */
                  <div className={`flex items-start gap-2 py-2 rounded-lg ${isAdmin ? `px-2 ${isDark ? "bg-gray-800/60 border border-gray-700" : "bg-gray-50 border border-gray-200"}` : ""}`}>
                    {isAdmin && <BlockControls id={item.id} idx={i} />}
                    <div className="flex-1 min-w-0">
                      {/* Content type toggle (admin only) */}
                      {isAdmin && (
                        <div className="flex items-center gap-1.5 mb-1.5">
                          <button
                            onClick={() => updateItem(item.id, "contentType", "paragraph")}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                              item.contentType !== "list"
                                ? "bg-pink-500 text-white"
                                : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            Paragraph
                          </button>
                          <button
                            onClick={() => updateItem(item.id, "contentType", "list")}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-full cursor-pointer whitespace-nowrap transition-colors ${
                              item.contentType === "list"
                                ? "bg-pink-500 text-white"
                                : isDark ? "bg-gray-700 text-gray-400 hover:bg-gray-600" : "bg-gray-100 text-gray-500 hover:bg-gray-200"
                            }`}
                          >
                            Bullet List
                          </button>
                        </div>
                      )}

                      {item.contentType === "list" ? (
                        <div>
                          {isAdmin ? (
                            <div className="space-y-1">
                              {(item.listItems ?? item.a.split("\n").filter(Boolean)).map((li, liIdx) => (
                                <div key={liIdx} className="flex items-center gap-1.5">
                                  <i className="ri-checkbox-circle-line text-pink-400 text-sm flex-shrink-0"></i>
                                  <AdminEditField
                                    value={li}
                                    onSave={(v) => {
                                      const listItems = (item.listItems ?? item.a.split("\n").filter(Boolean)).map((l, idx) => idx === liIdx ? v : l);
                                      updateItem(item.id, "listItems", listItems);
                                    }}
                                    className="flex-1"
                                    inputClassName="text-sm"
                                  >
                                    <span className={`text-sm ${subColor}`}>{li}</span>
                                  </AdminEditField>
                                  <button
                                    onClick={() => {
                                      const listItems = (item.listItems ?? item.a.split("\n").filter(Boolean)).filter((_, idx) => idx !== liIdx);
                                      updateItem(item.id, "listItems", listItems);
                                    }}
                                    className="w-5 h-5 flex items-center justify-center rounded-full bg-red-100 hover:bg-red-200 text-red-500 cursor-pointer flex-shrink-0"
                                    title="Remove list item"
                                  >
                                    <i className="ri-close-line text-xs"></i>
                                  </button>
                                </div>
                              ))}
                              <button
                                onClick={() => {
                                  const listItems = [...(item.listItems ?? item.a.split("\n").filter(Boolean)), "New list item"];
                                  updateItem(item.id, "listItems", listItems);
                                }}
                                className="text-xs text-pink-400 hover:text-pink-600 flex items-center gap-1 cursor-pointer mt-1"
                              >
                                <i className="ri-add-line"></i> Add item
                              </button>
                            </div>
                          ) : (
                            <ul className="space-y-1">
                              {(item.listItems ?? item.a.split("\n").filter(Boolean)).map((li, liIdx) => (
                                <li key={liIdx} className={`flex items-start gap-1.5 text-sm ${subColor}`}>
                                  <i className="ri-checkbox-circle-line text-pink-400 mt-0.5 flex-shrink-0"></i>
                                  <RichText html={li} as="span" />
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      ) : (
                        /* Paragraph mode */
                        isAdmin ? (
                          <AdminEditField
                            value={item.a}
                            onSave={(v) => updateItem(item.id, "a", v)}
                            multiline
                            richText
                            inputClassName="text-sm leading-relaxed"
                          >
                            <RichText html={item.a} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                          </AdminEditField>
                        ) : (
                          <RichText html={item.a} as="p" className={`text-sm leading-relaxed ${subColor}`} />
                        )
                      )}
                    </div>
                  </div>
                ) : item.type === "divider" ? (
                  /* Divider block */
                  <div className={`flex items-center gap-2 py-2 rounded-lg ${isAdmin ? `px-2 ${isDark ? "bg-gray-800/60 border border-gray-700" : "bg-gray-50 border border-gray-200"}` : ""}`}>
                    {isAdmin && <BlockControls id={item.id} idx={i} />}
                    <hr className={`flex-1 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`} />
                    {isAdmin && <span className={`text-[10px] font-semibold ${subColor} whitespace-nowrap`}>Divider</span>}
                  </div>
                ) : (
                  /* Q&A accordion (default) */
                  <div className={`rounded-xl border transition-all ${openIndex === i ? cardActive : cardBase}`}>
                    <div className="flex items-center">
                      {/* Move buttons for Q&A */}
                      {isAdmin && (
                        <div className="pl-2 flex-shrink-0 flex items-center gap-0.5">
                          <button
                            onClick={() => moveItem(i, -1)}
                            disabled={i === 0}
                            title="Move up"
                            className={iconBase}
                          >
                            <i className="ri-arrow-up-line text-xs"></i>
                          </button>
                          <button
                            onClick={() => moveItem(i, 1)}
                            disabled={i === data.items.length - 1}
                            title="Move down"
                            className={iconBase}
                          >
                            <i className="ri-arrow-down-line text-xs"></i>
                          </button>
                        </div>
                      )}
                      <button
                        className="flex-1 flex items-center justify-between px-4 py-5 text-left cursor-pointer"
                        onClick={() => setOpenIndex(openIndex === i ? null : i)}
                      >
                        {isAdmin ? (
                          <div className="flex-1 pr-4" onClick={(e) => e.stopPropagation()}>
                            <AdminEditField value={item.q} onSave={(v) => updateItem(item.id, "q", v)} inputClassName="font-bold text-base" richText>
                              <span className={`font-bold text-base ${headingColor}`}>{item.q}</span>
                            </AdminEditField>
                          </div>
                        ) : (
                          <span className={`font-bold text-base pr-4 ${headingColor}`}>
                            <RichText html={item.q} as="span" />
                          </span>
                        )}
                        <span
                          className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-colors ${
                            openIndex === i ? "bg-pink-500 text-white" : "bg-gray-100 text-gray-500"
                          }`}
                        >
                          <i className={openIndex === i ? "ri-subtract-line" : "ri-add-line"}></i>
                        </span>
                      </button>
                      {/* Delete button for Q&A */}
                      {isAdmin && (
                        <div className="flex items-center gap-1 pr-3 flex-shrink-0">
                          <button
                            onClick={() => removeItem(item.id)}
                            className="flex items-center gap-1 text-xs font-semibold bg-red-100 hover:bg-red-200 text-red-500 px-2 py-1 rounded-full cursor-pointer whitespace-nowrap"
                            title="Delete this Q&A"
                          >
                            <i className="ri-delete-bin-line text-xs"></i>
                            Delete
                          </button>
                        </div>
                      )}
                    </div>
                    {openIndex === i && (
                      <div className={`px-6 pb-5 text-sm leading-relaxed ${subColor}`}>
                        {isAdmin ? (
                          <AdminEditField value={item.a} onSave={(v) => updateItem(item.id, "a", v)} multiline richText inputClassName="text-sm leading-relaxed">
                            <RichText html={item.a} as="p" />
                          </AdminEditField>
                        ) : (
                          <RichText html={item.a} as="p" />
                        )}
                      </div>
                    )}
                  </div>
                )}

                {/* Insert bar after each item — only in admin */}
                {isAdmin && <InsertBar afterIndex={i} />}
              </div>
            ))}

            {/* Add New Question button */}
            {isAdmin && (
              <button
                onClick={() => addItem("qa")}
                className="w-full flex items-center justify-center gap-2 border-2 border-dashed border-pink-200 hover:border-pink-400 text-pink-400 hover:text-pink-500 rounded-xl py-4 text-sm font-semibold transition-colors cursor-pointer mt-2"
              >
                <i className="ri-add-line text-lg"></i>
                Add New Question
              </button>
            )}
          </div>
        </div>
      </div>
    </section>
  );

  // ── Insert bar between items ───────────────────────────────────────────
  function InsertBar({ afterIndex }: { afterIndex: number }) {
    return (
      <div className="flex items-center gap-1.5 py-2 pl-2">
        <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
        <span className="text-[10px] text-pink-500 font-bold uppercase tracking-wider">Insert:</span>
        <button onClick={() => addItem("qa", afterIndex)} className="text-[10px] bg-pink-500 text-white hover:bg-pink-600 px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap font-bold shadow-sm">Q&amp;A</button>
        <button onClick={() => addItem("heading", afterIndex)} className="text-[10px] bg-gray-200 text-gray-700 hover:bg-gray-300 px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap font-bold shadow-sm">Heading</button>
        <button onClick={() => addItem("text", afterIndex)} className="text-[10px] bg-gray-200 text-gray-700 hover:bg-gray-300 px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap font-bold shadow-sm">Text/List</button>
        <button onClick={() => addItem("divider", afterIndex)} className="text-[10px] bg-gray-200 text-gray-700 hover:bg-gray-300 px-2.5 py-1 rounded-full cursor-pointer whitespace-nowrap font-bold shadow-sm">Divider</button>
        <div className={`flex-1 h-px ${isDark ? "bg-gray-700" : "bg-gray-200"}`}></div>
      </div>
    );
  }
}